/**
 * Global fetch interceptor attaching `Authorization: Bearer <token>` to
 * every API request (roadmap#97).
 *
 * The codebase has ~90 raw fetch() call sites across 8 API modules with no
 * shared HTTP client; wrapping window.fetch once at bootstrap guarantees
 * coverage for all of them (and any future calls) without touching each
 * site. Only same-origin /api* requests and requests to the configured
 * REACT_APP_API_BASE_URL get the header.
 */

type TokenProvider = () => Promise<string | null>;

let installed = false;

export function shouldAttachToken(url: string): boolean {
    if (url.startsWith('/api')) {
        return true;
    }
    const apiBase = process.env.REACT_APP_API_BASE_URL;
    if (apiBase && apiBase.startsWith('http') && url.startsWith(apiBase)) {
        return true;
    }
    try {
        const resolved = new URL(url, window.location.origin);
        return (
            resolved.origin === window.location.origin &&
            resolved.pathname.startsWith('/api')
        );
    } catch {
        return false;
    }
}

export function installAuthFetch(getToken: TokenProvider): void {
    if (installed) {
        return;
    }
    installed = true;

    const originalFetch = window.fetch.bind(window);

    window.fetch = async (
        input: RequestInfo | URL,
        init?: RequestInit
    ): Promise<Response> => {
        const url =
            typeof input === 'string'
                ? input
                : input instanceof URL
                    ? input.toString()
                    : input.url;

        if (!shouldAttachToken(url)) {
            return originalFetch(input as RequestInfo, init);
        }

        const token = await getToken();
        if (!token) {
            return originalFetch(input as RequestInfo, init);
        }

        if (input instanceof Request && !init) {
            const headers = new Headers(input.headers);
            if (!headers.has('Authorization')) {
                headers.set('Authorization', `Bearer ${token}`);
            }
            return originalFetch(new Request(input, { headers }));
        }

        const headers = new Headers(
            init?.headers ?? (input instanceof Request ? input.headers : undefined)
        );
        if (!headers.has('Authorization')) {
            headers.set('Authorization', `Bearer ${token}`);
        }
        return originalFetch(input as RequestInfo, { ...init, headers });
    };
}

/** Test hook: allow re-installation after jest module resets. */
export function resetAuthFetchForTests(): void {
    installed = false;
}
